import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MetaService } from '@rma/generic/util/meta-service';
import { SafePipe } from '@rma/generic/util/pipes/dom-sanitisation';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BlogPost } from '../domain';
import { PostContentComponent } from '../ui-post-content/post-content.component';
import { PostShareButtonComponent } from '../ui-post-share-button/post-share-button.component';
import { PostSkeletonComponent } from '../ui-post-skeleton/post-skeleton.component';

@UntilDestroy()
@Component({
  selector: 'rma-blog-post',
  templateUrl: './blog-post.component.html',
  styleUrls: ['./blog-post.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PostShareButtonComponent, PostContentComponent, PostSkeletonComponent, AsyncPipe, SafePipe],
})
export class BlogPostComponent implements OnInit {
  public blogPost$: Observable<BlogPost>;
  public srcSet$: Observable<string>;

  public constructor(
    private readonly metaService: MetaService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    this.blogPost$ = this.route.data.pipe(map(({ post }) => post));

    this.srcSet$ = this.blogPost$.pipe(map((post) => this.getImageSrcs(post)));

    this.blogPost$.pipe(untilDestroyed(this)).subscribe(this.updateMetadata.bind(this));
  }

  private getImageSrcs(blogPost: BlogPost): string {
    if (!blogPost.featuredMedia?.sizes) {
      return '';
    }

    const { full, large, medium_large, medium } = blogPost.featuredMedia.sizes;

    return [full, large, medium_large, medium]
      .filter((size) => size)
      .map((size) => `${size.sourceUrl} ${size.width}w`)
      .join(', ');
  }

  private updateMetadata(blogPost: BlogPost): void {
    const { title, excerpt: description } = blogPost;
    const canonicalUrl = this.router.url;
    const baseSocialMeta = { title, description, imageUrl: blogPost.featuredMedia?.sizes.full.sourceUrl ?? '', canonicalUrl };

    this.metaService.update(baseSocialMeta, null, {
      facebook: {
        'image:height': blogPost.featuredMedia?.sizes.full.height.toString() ?? '',
        'image:width': blogPost.featuredMedia?.sizes.full.width.toString() ?? '',
      },
      twitter: { card: 'summary_large_image' },
    });
  }
}
